"use client";
import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthAction } from "@/lib/auth/actions";
import { useGetCallBackUrl } from "@/lib/helpers/hooks/useGetCallBackUrl";
import { DEFAULT_LOGIN_REDIRECT_PATH } from "@/lib/auth/routes";
import { signInSchema, SignInPayloadType } from "../types";
import { toast } from "react-toastify";
import { trackProductEvent } from "@/lib/analytics/product";

const INVALID_CREDENTIALS_MESSAGE =
  "The phone number or password you entered is incorrect.";
const GENERIC_SIGN_IN_ERROR_MESSAGE =
  "We could not sign you in. Please try again.";

const SIGN_IN_ERROR_MESSAGES: Record<string, string> = {
  CredentialsSignin: INVALID_CREDENTIALS_MESSAGE,
  "Invalid credentials": INVALID_CREDENTIALS_MESSAGE,
  [INVALID_CREDENTIALS_MESSAGE]: INVALID_CREDENTIALS_MESSAGE,
  [GENERIC_SIGN_IN_ERROR_MESSAGE]: GENERIC_SIGN_IN_ERROR_MESSAGE,
};

function getSignInErrorMessage(error?: string | null) {
  if (!error) return null;
  return SIGN_IN_ERROR_MESSAGES[error] ?? GENERIC_SIGN_IN_ERROR_MESSAGE;
}

export function useSignInForm() {
  const router = useRouter();
  const { authenticate, authenticateGoogle } = useAuthAction();
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [loadingGoogleSignIn, setLoadingGoogleSignIn] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const searchParams = useSearchParams();
  const signInError = searchParams.get("error");
  const [path, setPath] = useState(DEFAULT_LOGIN_REDIRECT_PATH);
  const callbackUrl = useGetCallBackUrl(path);
  const methods = useForm<SignInPayloadType>({
    resolver: zodResolver(signInSchema),
  });
  const { register, handleSubmit, formState } = methods;
  const { errors } = formState;

  type Response = { ok?: boolean; error?: string } | undefined;

  const onSubmit = handleSubmit(async (data: SignInPayloadType) => {
    setLoadingSignIn(true);
    const res: Response = await authenticate(
      {
        phone: data.phone,
        password: data.password,
      },
      callbackUrl,
    );
    handleLoginResponse(res, "phone");
  });

  async function signInWithGoogle() {
    setLoadingGoogleSignIn(true);
    const res: Response = await authenticateGoogle("signin", callbackUrl);
    handleLoginResponse(res, "google");
  }

  function handleLoginResponse(res: Response, method: "phone" | "google") {
    if (!res?.error) {
      trackProductEvent("Sign In", {
        Destination: path.includes("procure") ? "Procure" : "Hub",
        Method: method.includes("phone") ? "Phone" : "Google",
        Status: "Completed",
      });
      // toast.success("Successfully logged in!");
      // router.push(path);
    } else {
      setLoadingSignIn(false);
      setLoadingGoogleSignIn(false);
      toast.error(getSignInErrorMessage(res.error));
    }
  }

  useEffect(() => {
    const message = getSignInErrorMessage(signInError);
    if (message) toast.error(message);
  }, [signInError]);

  useEffect(() => {
    router.prefetch("/");
    router.prefetch("/procure");
  }, [router]);

  // useEffect(() => {
  //   setTimeout(() => setShowGoogle(true), 100);
  // }, []);

  return {
    errors,
    methods,
    loadingSignIn,
    loadingGoogleSignIn,
    onSubmit,
    path,
    setPath,
    register,
    signInWithGoogle,
    showGoogle,
  };
}
